import { DirectLinkType } from '@src/domains/direct-links';
import { getOrderSaleItems } from '@src/domains/orders';
import type { Transactable } from '@src/domains/transactables';

import type { CartItem, SaleItem, DetailedSaleItem } from '@src/core/buyerApiClient';

type Params = Transactable & {
  cartItems?: CartItem[];
};

/**
 * Where the purchased products are. Each shape carries them somewhere else, and the planne link is
 * the one that never carries them at all: it points at the sale or at the cart, and a payment link
 * has no product, only an amount. The precedence is the one of `resolveTransactableEntity`, so the
 * products reported are the ones of the purchase being charged.
 */
export const resolveCheckoutItems = ({
  order,
  sale,
  saleChange,
  directLink,
  cartItems,
}: Params): (DetailedSaleItem | SaleItem | CartItem)[] => {
  if (saleChange?.saleItem) {
    return [saleChange.saleItem];
  }

  const orderItems = order?.sales ? getOrderSaleItems(order) : [];

  if (orderItems.length) {
    return orderItems;
  }

  if (sale?.items?.length) {
    return sale.items;
  }

  // Only a cart link ends up in the cart. `initializeStore` calls `setupCart()` on every page, so
  // whatever cart the buyer left on the seller's site is in the store while a sale or payment link
  // is open, and those products are not what is being charged.
  if (directLink && directLink.type !== DirectLinkType.Cart) {
    return [];
  }

  return cartItems ?? [];
};

export default resolveCheckoutItems;
